import type {RGBA} from '@Utils/color.util';
import {deriveGradientSecondColor, hexToRgba, lightenDarkenColor} from '@Utils/color.util';
import {assertNever} from '@Utils/assert-never.util';
import type {FillObject, GradientFillObject, GradientStop} from '@PosterWhiteboard/classes/fill.types';
import {
  createSolidFill,
  createTransparentFill,
  DEFAULT_FILL_COLOR,
  FillTypes,
  getEvenStops,
  getStoredFillColors,
  hasFill,
  isGradientFillObject,
  RadialGradientShape,
} from '@PosterWhiteboard/classes/fill.types';
import {DEFAULT_BACKGROUND_COLOR, DEFAULT_SECONDARY_BACKGROUND_COLOR} from '@PosterWhiteboard/page/page.types';
import {PMW_SHAPE_DEFAULT_FILL} from '@PosterWhiteboard/items/vector-item/vector-item.types';
import {QR_DEFAULT_BACKGROUND_COLOR} from '@Libraries/qr-code/qr-code.types';

/** What a fill paints. It decides how a fill switched to a gradient there picks its colors and lays them out. */
export enum FillSurface {
  /** Text, its word gradients, subtitles and their backgrounds. */
  TEXT = 'text',
  /** The box behind a text item or text slide. Laid out like text; only the color it is switched on with differs. */
  TEXT_BACKGROUND = 'text-background',
  /** Rectangles and lines. */
  SHAPE = 'shape',
  /** A color page background. */
  BACKGROUND = 'background',
  VECTOR = 'vector',
  QR = 'qr',
}

interface StopOffsetRange {
  start: number;
  end: number;
}

const TOP_TO_BOTTOM_ANGLE = 180;
const BOX_CENTER = 0.5;
const LONGER_SIDE_RADIUS = 1;
const EDGE_TO_EDGE: StopOffsetRange = {start: 0, end: 1};
/** Text and shapes reach their last linear color three quarters of the way across. */
const TEXT_AND_SHAPE_LINEAR: StopOffsetRange = {start: 0, end: 0.75};
/** Text and shapes keep their first radial color solid out to a tenth of the radius. */
const TEXT_AND_SHAPE_RADIAL: StopOffsetRange = {start: 0.1, end: 1};
/** The gray a new text box's background is switched on with. */
const TEXT_BACKGROUND_DEFAULT_COLOR: RGBA = [185, 185, 185, 1];
/** Offsets and coordinates this close are the same: a stock layout is recomputed here, not read back, so a stored one may differ in its last bit. */
const LAYOUT_TOLERANCE = 1e-9;

/**
 * Where a surface's gradients put their first and last stop: the layout every gradient saved before v81 was drawn with. Vectors and QR codes
 * draw gradients with their own formulas from the colors alone, so theirs is a plain edge-to-edge default.
 */
const getStopOffsetRange = (surface: FillSurface, fillType: GradientFillObject['fillType']): StopOffsetRange => {
  switch (surface) {
    case FillSurface.TEXT:
    case FillSurface.TEXT_BACKGROUND:
    case FillSurface.SHAPE:
      return fillType === FillTypes.LINEAR_GRADIENT ? TEXT_AND_SHAPE_LINEAR : TEXT_AND_SHAPE_RADIAL;
    case FillSurface.BACKGROUND:
    case FillSurface.VECTOR:
    case FillSurface.QR:
      return EDGE_TO_EDGE;
    default:
      return assertNever(surface);
  }
};

/** A shape's radial gradient is an ellipse that stretches with the box; every other surface's is a circle. */
const getRadialShape = (surface: FillSurface): RadialGradientShape => {
  return surface === FillSurface.SHAPE ? RadialGradientShape.ELLIPSE : RadialGradientShape.CIRCLE;
};

/** A new gradient of `fillType` over `stops`, laid out the way the surface draws one. */
const createGradientFill = (fillType: GradientFillObject['fillType'], stops: GradientStop[], surface: FillSurface): GradientFillObject => {
  if (fillType === FillTypes.LINEAR_GRADIENT) {
    return {fillType, stops, angle: TOP_TO_BOTTOM_ANGLE};
  }
  return {fillType, stops, center: {x: BOX_CENTER, y: BOX_CENTER}, radius: LONGER_SIDE_RADIUS, shape: getRadialShape(surface)};
};

/** A new gradient of `fillType` painting `colors`, laid out the way the surface draws one. */
export const createGradientFillForSurface = (fillType: GradientFillObject['fillType'], colors: RGBA[], surface: FillSurface): GradientFillObject => {
  const {start, end} = getStopOffsetRange(surface, fillType);
  return createGradientFill(fillType, getEvenStops(colors, start, end), surface);
};

/** A fill of `fillType` painting `colors`; a gradient is laid out the way the surface draws one. */
export const createFillForSurface = (fillType: FillTypes, colors: RGBA[], surface: FillSurface): FillObject => {
  switch (fillType) {
    case FillTypes.NONE:
      return createTransparentFill(colors.length > 0 ? colors[0] : null);
    case FillTypes.SOLID:
      return createSolidFill(colors[0]);
    case FillTypes.LINEAR_GRADIENT:
    case FillTypes.RADIAL_GRADIENT:
      return createGradientFillForSurface(fillType, colors, surface);
    default:
      return assertNever(fillType);
  }
};

/**
 * Adapts a page background's colors to a target fill type: keeps them when widening (solid → gradient gains the default secondary color),
 * trims to the first when narrowing to solid or transparent; a background with no colors starts from the default ones.
 */
export const getBackgroundColorsForFillType = (newType: FillTypes, existingColors: RGBA[]): RGBA[] => {
  if (newType === FillTypes.LINEAR_GRADIENT || newType === FillTypes.RADIAL_GRADIENT) {
    if (existingColors.length >= 2) {
      return existingColors;
    }
    const primary = existingColors[0] ?? hexToRgba(DEFAULT_BACKGROUND_COLOR);
    return [primary, hexToRgba(DEFAULT_SECONDARY_BACKGROUND_COLOR)];
  }
  return [existingColors[0] ?? hexToRgba(DEFAULT_BACKGROUND_COLOR)];
};

/**
 * The fill switched to another type. A fill turned off keeps its first color while editing, so turning it back on restores it; one loaded
 * turned off has none and turns on to the surface's default color. A solid keeps the first color; a gradient lays its colors out the way the
 * surface draws the new type, as every gradient was redrawn before v81: one switched from the other gradient type keeps its colors, and one
 * made from a single color gains a second.
 */
export const withFillType = (fill: FillObject, newType: FillTypes, surface: FillSurface): FillObject => {
  if (newType === fill.fillType) {
    return fill;
  }
  const colors = getColorsForNewType(fill, newType, surface);
  switch (newType) {
    case FillTypes.NONE:
      return createTransparentFill(colors[0]);
    case FillTypes.SOLID:
      return createSolidFill(colors[0]);
    case FillTypes.LINEAR_GRADIENT:
    case FillTypes.RADIAL_GRADIENT:
      return createGradientFillForSurface(newType, colors, surface);
    default:
      return assertNever(newType);
  }
};

/** The fill switched off: it paints nothing and keeps its first color to return to while editing. */
export const withFillTurnedOff = (fill: FillObject, surface: FillSurface): FillObject => {
  return withFillType(fill, FillTypes.NONE, surface);
};

/**
 * The fill switched on: a fill that paints nothing paints the color it kept while editing, or the surface's default color (a page background
 * always starts from its default colors, see {@link getBackgroundColorsForFillType}), and one that already paints stays as it is.
 */
export const withFillTurnedOn = (fill: FillObject, surface: FillSurface): FillObject => {
  return hasFill(fill) ? fill : withFillType(fill, FillTypes.SOLID, surface);
};

/**
 * The fill moved onto another surface: a pasted style, a replaced shape, a rectangle turned into the page background or back. A gradient in the
 * layout some surface gives a new gradient takes this surface's layout instead, as every gradient was redrawn for the surface it landed on
 * before v81; a layout set by hand travels with the fill.
 */
export const withLayoutForSurface = (fill: FillObject, surface: FillSurface): FillObject => {
  return isGradientFillObject(fill) && !hasStockLayout(fill) ? fill : withStockLayoutForSurface(fill, surface);
};

/** The fill with a gradient keeping its colors and laid out the way the surface lays out a new one, whatever layout it had. */
export const withStockLayoutForSurface = (fill: FillObject, surface: FillSurface): FillObject => {
  return isGradientFillObject(fill) ? createGradientFillForSurface(fill.fillType, getStoredFillColors(fill), surface) : fill;
};

const getColorsForNewType = (fill: FillObject, newType: FillTypes, surface: FillSurface): RGBA[] => {
  const storedColors = getStoredFillColors(fill);
  if (surface === FillSurface.BACKGROUND) {
    return getBackgroundColorsForFillType(newType, fill.fillType === FillTypes.NONE ? [] : storedColors);
  }
  const colors = storedColors.length > 0 ? storedColors : [getDefaultFillColor(surface)];
  if ((newType === FillTypes.LINEAR_GRADIENT || newType === FillTypes.RADIAL_GRADIENT) && !isGradientFillObject(fill)) {
    return [colors[0], getSecondGradientColor(colors[0], surface)];
  }
  return colors;
};

/** The color a fill switched on paints when it kept none: the one a new item of that kind starts with. */
export const getDefaultFillColor = (surface: FillSurface): RGBA => {
  switch (surface) {
    case FillSurface.TEXT:
      return DEFAULT_FILL_COLOR;
    case FillSurface.TEXT_BACKGROUND:
      return TEXT_BACKGROUND_DEFAULT_COLOR;
    case FillSurface.SHAPE:
    case FillSurface.VECTOR:
      return PMW_SHAPE_DEFAULT_FILL;
    case FillSurface.QR:
      return QR_DEFAULT_BACKGROUND_COLOR;
    case FillSurface.BACKGROUND:
      return hexToRgba(DEFAULT_BACKGROUND_COLOR);
    default:
      return assertNever(surface);
  }
};

const getSecondGradientColor = (firstColor: RGBA, surface: FillSurface): RGBA => {
  switch (surface) {
    case FillSurface.SHAPE:
    case FillSurface.VECTOR:
      return deriveGradientSecondColor(firstColor);
    case FillSurface.TEXT:
    case FillSurface.TEXT_BACKGROUND:
    case FillSurface.QR:
    case FillSurface.BACKGROUND:
      return hexToRgba(lightenDarkenColor(firstColor));
    default:
      return assertNever(surface);
  }
};

/** Whether the gradient is laid out the way some surface lays out a new one: every gradient saved before v81 and every one made since without a gradient editor. */
const hasStockLayout = (fill: GradientFillObject): boolean => {
  const colors = getStoredFillColors(fill);
  return Object.values(FillSurface).some((surface) => isSameLayout(fill, createGradientFillForSurface(fill.fillType, colors, surface)));
};

const isSameLayout = (fill: GradientFillObject, stockFill: GradientFillObject): boolean => {
  return haveSameStopOffsets(fill.stops, stockFill.stops) && haveSameGeometry(fill, stockFill);
};

const haveSameStopOffsets = (stops: GradientStop[], stockStops: GradientStop[]): boolean => {
  return stops.length === stockStops.length && stops.every((stop, index) => isSameNumber(stop.offset, stockStops[index].offset));
};

const haveSameGeometry = (fill: GradientFillObject, stockFill: GradientFillObject): boolean => {
  if (fill.fillType === FillTypes.LINEAR_GRADIENT) {
    return stockFill.fillType === FillTypes.LINEAR_GRADIENT && isSameNumber(fill.angle, stockFill.angle);
  }
  return (
    stockFill.fillType === FillTypes.RADIAL_GRADIENT &&
    fill.shape === stockFill.shape &&
    isSameNumber(fill.radius, stockFill.radius) &&
    isSameNumber(fill.center.x, stockFill.center.x) &&
    isSameNumber(fill.center.y, stockFill.center.y)
  );
};

const isSameNumber = (value: number, stockValue: number): boolean => {
  return Math.abs(value - stockValue) < LAYOUT_TOLERANCE;
};
